import {useGetFeatureTogglesQuery} from '../services/schedulerInternal';
import {getDefaultValueForType} from '../Utils';
import {FEATURE_TOGGLES} from '../Utils/consts';

/**
 * Hook to fetch and access feature toggles
 * @param {Array} toggleKeys - Optional array of toggle keys to fetch. If not provided, fetches all configured toggles.
 * @return {Object} - Object containing toggle values and loading/error states
 */
export const useFeatureToggles = (toggleKeys = null) => {
  // Determine which toggles to fetch
  const togglesToFetch = toggleKeys ?
    toggleKeys.map((key) => {
      const toggle = Object.values(FEATURE_TOGGLES).find((t) => t.key === key);
      if (!toggle) {
        console.warn(`Feature toggle with key "${key}" not found in FEATURE_TOGGLES`);
        return null;
      }
      return {
        configKey: toggle.key,
        configType: toggle.type,
      };
    }).filter(Boolean) :
    Object.values(FEATURE_TOGGLES).map((toggle) => ({
      configKey: toggle.key,
      configType: toggle.type,
    }));

  // Use RTK Query's built-in caching with refetchOnMountOrArgChange
  const {
    data,
    error,
    isError,
    isLoading,
    refetch,
  } = useGetFeatureTogglesQuery(togglesToFetch, {
    skip: togglesToFetch.length === 0, // Skip if no toggle to fetch
    refetchOnMountOrArgChange: 5 * 60, // 5 minutes in seconds
  });

  return {
    data: data || {},
    loading: isLoading,
    error: isError ? error : null,
    refetch,
  };
};

/**
 * Hook to get a specific feature toggle value
 * @param {Object} toggleConfig - Configuration object from FEATURE_TOGGLES
 * @return {any} - The toggle value with fallback to default
 */
export const useFeatureToggle = (toggleConfig) => {
  const {data} = useFeatureToggles();
  const value = data[toggleConfig.key];

  if (value !== undefined) {
    return value;
  }

  return getDefaultValueForType(toggleConfig.type, toggleConfig.defaultValue);
};

/**
 * Convenience hook for Zimbra integration toggle
 * @return {boolean} - Whether Zimbra integration is enabled
 */
export const useZimbraIntegrationEnabled = () => {
  return useFeatureToggle(FEATURE_TOGGLES.SCHEDULER_ZIMBRA_INTEGRATION_ENABLED);
};

/**
 * Convenience hook for AIC meeting settings toggle
 * @return {boolean} - Whether AIC meeting settings are enabled
 */
export const useAICMeetingSettingsEnabled = () => {
  return useFeatureToggle(FEATURE_TOGGLES.SCHEDULER_AIC_MEETING_SETTINGS_ENABLED);
};

/**
 * Convenience hook for Delegate settings feature toggle
 * @return {boolean} - Whether Delegate feature is enabled
 */
export const useDelegateEnabled = () => {
  return useFeatureToggle(FEATURE_TOGGLES.SCHEDULER_DELEGATE_ENABLED);
};
